/**
 * Draft PR23: real saved-group additional-analysis preflight/start/reload/result UI.
 *
 * The component never invents progress or completion: it shows the API job status and
 * polls only the exact job id it created/loaded. Semantic analysis is always shown as
 * not evaluated while no product provider is configured.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  type GroupAnalysisRun,
  type JobDetail,
  type OpportunityGroup,
  type OpportunityGroupSource,
} from "../api/client";
import {
  parseGroupAnalysisPreflight,
  parseGroupAnalysisResult,
  parseGroupAnalysisRun,
  parseGroupAnalysisRunPage,
  sameGroupAnalysisSource,
} from "./groupAnalysis";
import type { GroupAnalysisLoader } from "./groupAnalysis";

const TERMINAL = new Set(["succeeded", "failed", "cancelled", "killed", "needs_review"]);
const GROUP_CHANGED_TEXT =
  "Grupa je u međuvremenu promijenjena (verzija, izvor ili odabir se ne podudara). Zatvorite preflight i ponovno ga otvorite iz točno spremljene grupe.";
const DIRTY_PREFLIGHT_TEXT =
  "Grupa, naziv, bilješka ili odabir imaju nespremljene izmjene. Spremite ih i ponovno otvorite preflight; posao neće pokrenuti prethodno spremljenu verziju.";
const SOURCE_PREFLIGHT_TEXT =
  "Grupa pripada drugoj izvornoj analizi. Otvorite njezinu izvornu analizu i ponovno otvorite preflight; odabir se ne prebacuje na trenutačni rezultat.";
const BUSY_PREFLIGHT_TEXT =
  "Spremanje grupe je u tijeku. Pričekajte da se spremljena verzija potvrdi prije pokretanja posla.";

type SavedTarget = {
  id: string;
  version: number;
  source: OpportunityGroupSource;
  organizationIds: string[];
};

class GroupAnalysisConflict extends Error {}

function targetOf(group: OpportunityGroup): SavedTarget {
  return {
    id: group.id,
    version: group.version,
    source: group.source,
    organizationIds: [...group.organization_ids],
  };
}

function sameStrings(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

function targetMatchesGroup(target: SavedTarget, group: OpportunityGroup): boolean {
  return target.id === group.id
    && target.version === group.version
    && sameGroupAnalysisSource(target.source, group.source)
    && sameStrings(target.organizationIds, group.organization_ids);
}

function preflightMatchesGroup(
  preflight: NonNullable<ReturnType<typeof parseGroupAnalysisPreflight>>,
  group: OpportunityGroup,
): boolean {
  return preflight.group.id === group.id
    && preflight.group.version === group.version
    && sameGroupAnalysisSource(preflight.source, group.source)
    && sameStrings(preflight.group.organization_ids, group.organization_ids);
}
const STATUS_HR: Record<string, string> = {
  queued: "u redu",
  running: "izvršava se",
  awaiting_approval: "čeka odobrenje",
  awaiting_budget_approval: "čeka budžet",
  succeeded: "uspješno",
  failed: "neuspješno",
  cancelled: "otkazano",
  killed: "zaustavljeno",
  needs_review: "za pregled",
};

function errorText(error: unknown): string {
  if (error instanceof GroupAnalysisConflict) {
    return error.message;
  }
  if (error instanceof ApiError) {
    if (error.detail === "group_version_conflict") {
      return "Grupa je u međuvremenu promijenjena. Osvježite popis i ponovno učitajte grupu prije pokretanja.";
    }
    if (error.detail === "idempotency_conflict") {
      return "Idempotency ključ pripada drugom zahtjevu. Zatražite novi preflight; nije stvoren drugi posao.";
    }
    return error.messageHr;
  }
  return "Dodatnu analizu nije moguće provjeriti. Pokušajte osvježiti.";
}

function formatMicros(value: number): string {
  return `$${(value / 1_000_000).toFixed(2)}`;
}

export function GroupAnalysisPanel({
  projectId,
  group,
  dirty,
  groupBusy,
  sourceMatches,
  hasUnsavedSelection,
  loader,
  onSaveAndContinue,
}: {
  projectId: string;
  group: OpportunityGroup | null;
  dirty: boolean;
  groupBusy: boolean;
  sourceMatches: boolean;
  hasUnsavedSelection: boolean;
  loader?: GroupAnalysisLoader;
  onSaveAndContinue: () => Promise<OpportunityGroup | null>;
}) {
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflight, setPreflight] = useState<ReturnType<typeof parseGroupAnalysisPreflight>>(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [job, setJob] = useState<JobDetail | null>(null);
  const [jobRun, setJobRun] = useState<GroupAnalysisRun | null>(null);
  const [runs, setRuns] = useState<GroupAnalysisRun[]>([]);
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const idempotencyKey = useRef<string | null>(null);
  const pendingTarget = useRef<SavedTarget | null>(null);
  const pendingSave = useRef<{ projectId: string; baselineGroupId: string | null } | null>(null);
  const confirmLock = useRef(false);
  const operationRef = useRef<object | null>(null);
  const jobRunRef = useRef<GroupAnalysisRun | null>(null);
  const preflightRef = useRef<ReturnType<typeof parseGroupAnalysisPreflight>>(null);
  const preflightOpenRef = useRef(false);
  const projectRef = useRef(projectId);
  const latestProjectRef = useRef(projectId);
  const groupRef = useRef<OpportunityGroup | null>(group);
  preflightRef.current = preflight;
  preflightOpenRef.current = preflightOpen;
  latestProjectRef.current = projectId;
  groupRef.current = group;
  const groupKey = group ? `${group.id}:${group.version}:${group.source.job_id}:${group.source.decisions_hash}` : "";

  const reset = useCallback(() => {
    generation.current += 1;
    setPreflightOpen(false);
    setPreflight(null);
    setQuestion("");
    setBusy(false);
    setError("");
    setNotice("");
    setJob(null);
    setJobRun(null);
    jobRunRef.current = null;
    setRuns([]);
    setLoading(false);
    idempotencyKey.current = null;
    confirmLock.current = false;
    pendingTarget.current = null;
    pendingSave.current = null;
  }, []);

  const loadJob = useCallback(async (
    jobId: string, current: number, expectedRun: GroupAnalysisRun,
    expectedSource: OpportunityGroupSource, accept?: () => boolean,
  ) => {
    if (!loader) return null;
    const detail = await loader.getJob(projectId, jobId);
    if (current !== generation.current || projectId !== latestProjectRef.current) return null;
    if (detail.id !== jobId) {
      throw new GroupAnalysisConflict(
        "Poslužitelj nije vratio zatraženi posao. Rezultat se ne prikazuje.",
      );
    }
    if (detail.project_id !== projectId) {
      throw new GroupAnalysisConflict(
        "Posao pripada drugom projektu. Rezultat se ne prikazuje.",
      );
    }
    if (detail.workflow !== "megabooker.group_additional_analysis") {
      throw new GroupAnalysisConflict(
        "Posao nije dodatna analiza grupe. Rezultat se ne prikazuje.",
      );
    }
    const identity = detail.group_analysis;
    const expected = expectedRun.group_analysis;
    if (
      !identity
      || identity.group_id !== expected.group_id
      || identity.group_version !== expected.group_version
      || identity.snapshot_hash !== expected.snapshot_hash
      || identity.request_fingerprint !== expected.request_fingerprint
      || !identity.source
      || !sameGroupAnalysisSource(identity.source, expectedSource)
    ) {
      throw new GroupAnalysisConflict(
        "Posao ili rezultat nisu povezani s točnom spremljenom grupom, verzijom i izvorom. Prikaz je blokiran.",
      );
    }
    if (detail.result !== undefined && detail.result !== null) {
      const parsed = parseGroupAnalysisResult(
        detail.result, projectId, expected.group_id, {
          groupVersion: expected.group_version,
          source: expectedSource,
          snapshotHash: expected.snapshot_hash,
        },
      );
      if (!parsed) {
        throw new GroupAnalysisConflict(
          "Rezultat posla nije prošao strogu provjeru identiteta i izvora. Nalazi se ne prikazuju.",
        );
      }
    }
    if (accept && !accept()) return null;
    // A poll must never regress an already terminal state for this exact job.
    setJob((previous) => (
      previous && previous.id === detail.id
        && TERMINAL.has(previous.status) && !TERMINAL.has(detail.status)
        ? previous
        : detail
    ));
    setJobRun(expectedRun);
    jobRunRef.current = expectedRun;
    return detail;
  }, [loader, projectId]);

  const loadRuns = useCallback(async (
    target: OpportunityGroup,
    options?: {
      preserveError?: boolean;
      autoSelect?: boolean;
      selectRunId?: string;
      generation?: number;
    },
  ) => {
    if (!loader) return;
    const operationProject = projectId;
    // A list refresh that follows an already-current job must not retire the
    // polling generation that job was accepted under.
    const current = options?.generation ?? (generation.current += 1);
    setLoading(true);
    if (!options?.preserveError) setError("");
    try {
      const raw = await loader.listGroupAnalysisRuns(projectId, target.id, { limit: 20 });
      if (current !== generation.current || operationProject !== latestProjectRef.current) return;
      const page = parseGroupAnalysisRunPage(raw, projectId, target.id, {
        source: target.source,
      });
      if (!page) throw new Error("group_analysis_runs_invalid");
      setRuns(page.items);
      if (options?.autoSelect === false) return;
      const selected = options?.selectRunId
        ? page.items.find((item) => item.id === options.selectRunId)
        : page.items[0];
      if (selected) {
        await loadJob(selected.id, current, selected, target.source);
      }
    } catch (err) {
      if (current === generation.current && operationProject === latestProjectRef.current) {
        setError(errorText(err));
      }
    } finally {
      if (current === generation.current && operationProject === latestProjectRef.current) {
        setLoading(false);
      }
    }
  }, [loader, projectId, loadJob]);

  useEffect(() => {
    const projectChanged = projectRef.current !== projectId;
    projectRef.current = projectId;
    const pending = pendingTarget.current;
    const saving = pendingSave.current;
    const currentPreflight = preflightRef.current;
    const currentPreflightOpen = preflightOpenRef.current;
    const ownTarget = Boolean(pending && group && targetMatchesGroup(pending, group));
    const ownPreflight = Boolean(
      currentPreflightOpen && currentPreflight && group
      && preflightMatchesGroup(currentPreflight, group),
    );
    const ownSave = Boolean(
      !projectChanged && saving && saving.baselineGroupId !== null
      && group && group.id === saving.baselineGroupId,
    );
    const sameIdChanged = Boolean(
      currentPreflightOpen && currentPreflight && group
      && currentPreflight.group.id === group.id && !ownPreflight,
    );
    if (!projectChanged && (ownTarget || ownPreflight || ownSave)) {
      return;
    }
    if (projectChanged || sameIdChanged) {
      reset();
      if (sameIdChanged) setError(GROUP_CHANGED_TEXT);
      if (group && sourceMatches && loader) {
        void loadRuns(group, { preserveError: sameIdChanged });
      }
      return;
    }
    reset();
    if (group && sourceMatches && loader) {
      void loadRuns(group);
    }
    // `groupKey` binds this effect to the exact persisted group/source identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, groupKey, sourceMatches, loader, reset, loadRuns]);

  useEffect(() => () => {
    generation.current += 1;
  }, []);

  useEffect(() => {
    const jobId = job?.id;
    const jobStatus = job?.status;
    if (!jobId || !jobStatus || !loader || TERMINAL.has(jobStatus)) return;
    const current = generation.current;
    const run = jobRunRef.current;
    if (!run) return;
    const expectedSource = run.group_analysis.source ?? group?.source;
    if (!expectedSource) return;
    let stopped = false;
    let inFlight = false;
    let requestSeq = 0;
    let appliedSeq = 0;
    let timer = 0;
    const stop = () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
    };
    timer = window.setInterval(() => {
      // One request at a time, plus a monotonic per-job sequence: an older
      // response can never replace a newer status (including a terminal one).
      if (stopped || inFlight) return;
      inFlight = true;
      const seq = ++requestSeq;
      void (async () => {
        try {
          const detail = await loadJob(jobId, current, run, expectedSource, () => {
            if (stopped || seq <= appliedSeq) return false;
            appliedSeq = seq;
            return true;
          });
          if (detail && !stopped && current === generation.current) setError("");
        } catch (err) {
          if (stopped || current !== generation.current) return;
          if (err instanceof GroupAnalysisConflict) {
            // An identity/source conflict is not transient: stop polling and
            // remove the now-untrusted job display, keeping only the bounded
            // masked conflict message.
            stop();
            setJob(null);
            setJobRun(null);
            jobRunRef.current = null;
            setError(errorText(err));
            return;
          }
          // Transient failures keep polling; the next authorized success clears
          // this error instead of leaving it stale.
          setError(errorText(err));
        } finally {
          inFlight = false;
        }
      })();
    }, 1500);
    return stop;
    // `job.id`/status bind the interval to one exact job; `group?.source` only
    // supplies the expected source when the run itself lacks one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status, loader, loadJob, group?.source]);

  const openPreflight = async (target: SavedTarget) => {
    if (!loader) return;
    const operationProject = projectId;
    const current = ++generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const raw = await loader.getGroupAnalysisPreflight(projectId, target.id);
      if (current !== generation.current || operationProject !== latestProjectRef.current) return;
      const parsed = parseGroupAnalysisPreflight(raw, projectId, target.id, {
        groupVersion: target.version,
        source: target.source,
        organizationIds: target.organizationIds,
      });
      if (!parsed) throw new GroupAnalysisConflict(GROUP_CHANGED_TEXT);
      setPreflight(parsed);
      setQuestion(parsed.question.suggested.slice(0, parsed.question.max_length));
      setPreflightOpen(true);
      idempotencyKey.current = null;
    } catch (err) {
      if (current === generation.current && operationProject === latestProjectRef.current) {
        setError(errorText(err));
      }
    } finally {
      if (current === generation.current && operationProject === latestProjectRef.current) {
        setBusy(false);
      }
    }
  };

  const startFlow = async () => {
    if (!loader || busy || groupBusy) return;
    if (!group && !hasUnsavedSelection) return;
    setError("");
    setNotice("");
    if (!group || dirty) {
      const operation = {};
      operationRef.current = operation;
      const operationProject = latestProjectRef.current;
      const baselineGroupId = group?.id ?? null;
      const saveEntry = { projectId: operationProject, baselineGroupId };
      pendingSave.current = saveEntry;
      setBusy(true);
      let target: SavedTarget | null = null;
      try {
        const saved = await onSaveAndContinue();
        if (operationRef.current !== operation || operationProject !== latestProjectRef.current) return;
        if (!saved) return;
        if (
          saved.project_id !== operationProject
          || (baselineGroupId !== null && saved.id !== baselineGroupId)
        ) {
          throw new GroupAnalysisConflict(GROUP_CHANGED_TEXT);
        }
        const latest = groupRef.current;
        // A create (baseline null) is ours only while the parent still has no
        // group or has installed the exact group this write returned. Any other
        // group is a genuine navigation and wins; C is then ignored.
        if (latest && latest.id !== saved.id) return;
        target = targetOf(saved);
        pendingTarget.current = target;
        pendingSave.current = null;
        await openPreflight(target);
      } catch (err) {
        if (operationRef.current !== operation || operationProject !== latestProjectRef.current) return;
        const latest = groupRef.current;
        if (baselineGroupId === null) {
          // A failed create must not surface in a group the user navigated to.
          if (latest !== null) return;
        } else if (!latest || latest.id !== baselineGroupId) {
          return;
        }
        setError(errorText(err));
      } finally {
        // Only clear the refs/busy this exact operation owns; a newer project or
        // navigation flow may already have installed its own state.
        if (pendingSave.current === saveEntry) pendingSave.current = null;
        if (target && pendingTarget.current === target) pendingTarget.current = null;
        if (operationRef.current === operation) {
          operationRef.current = null;
          setBusy(false);
        }
      }
      return;
    }
    const operation = {};
    operationRef.current = operation;
    const target = targetOf(group);
    pendingTarget.current = target;
    try {
      await openPreflight(target);
    } finally {
      if (pendingTarget.current === target) pendingTarget.current = null;
      if (operationRef.current === operation) operationRef.current = null;
    }
  };

  const confirmStart = async () => {
    if (!loader || !preflight || busy || confirmLock.current) return;
    if (!group || !preflightMatchesGroup(preflight, group)) {
      setPreflightOpen(false);
      setError(GROUP_CHANGED_TEXT);
      return;
    }
    if (groupBusy) {
      setError(BUSY_PREFLIGHT_TEXT);
      return;
    }
    if (!sourceMatches) {
      setError(SOURCE_PREFLIGHT_TEXT);
      return;
    }
    if (dirty) {
      setError(DIRTY_PREFLIGHT_TEXT);
      return;
    }
    confirmLock.current = true;
    const operation = {};
    operationRef.current = operation;
    const operationProject = latestProjectRef.current;
    const current = generation.current;
    if (idempotencyKey.current === null) {
      idempotencyKey.current = `gda-${preflight.group.id}-${preflight.group.version}-${Date.now()}-${Math.floor(Math.random() * 100_000)}`;
    }
    setBusy(true);
    setError("");
    try {
      const created = await loader.startGroupAnalysis(projectId, preflight.group.id, {
        group_version: preflight.group.version,
        question,
        idempotency_key: idempotencyKey.current,
      });
      if (
        current !== generation.current
        || operationProject !== latestProjectRef.current
        || operationRef.current !== operation
      ) return;
      const run = parseGroupAnalysisRun(created, projectId, preflight.group.id, {
        groupVersion: preflight.group.version,
        source: preflight.source,
      });
      if (!run) {
        throw new GroupAnalysisConflict(
          "Poslužitelj nije potvrdio točnu spremljenu verziju i izvor posla. Posao nije prihvaćen kao trenutačni.",
        );
      }
      setPreflightOpen(false);
      setNotice("Posao je predan u trajni red. Prikazujem stvarni status poslužitelja.");
      idempotencyKey.current = null;
      const detail = await loadJob(run.id, current, run, preflight.source, () => (
        operationRef.current === operation
      ));
      const latest = groupRef.current;
      // Refresh the list for the same group this operation started, without
      // auto-opening items[0]: the run just created stays selected. A stale
      // closure or a navigation to another group must never reload its history.
      if (
        detail
        && current === generation.current
        && operationProject === latestProjectRef.current
        && latest
        && preflightMatchesGroup(preflight, latest)
      ) {
        await loadRuns(latest, { autoSelect: false, generation: current });
      }
    } catch (err) {
      if (
        current === generation.current
        && operationProject === latestProjectRef.current
        && operationRef.current === operation
      ) setError(errorText(err));
    } finally {
      if (operationRef.current === operation) {
        confirmLock.current = false;
        // `loadRuns` above retires the previous generation intentionally. This
        // local pending flag belongs to the click, not to that list generation.
        setBusy(false);
      }
    }
  };

  const openRun = async (jobId: string) => {
    const run = runs.find((item) => item.id === jobId);
    const expectedSource = group?.source ?? run?.group_analysis.source;
    if (!run || !expectedSource) {
      setError(GROUP_CHANGED_TEXT);
      return;
    }
    const operationProject = projectId;
    const current = ++generation.current;
    setError("");
    try {
      await loadJob(jobId, current, run, expectedSource);
    } catch (err) {
      if (current === generation.current && operationProject === latestProjectRef.current) {
        setError(errorText(err));
      }
    }
  };

  const activeRun = jobRun ?? (job?.group_analysis ? {
    id: job.id,
    status: job.status,
    group_analysis: job.group_analysis,
  } as GroupAnalysisRun : null);
  const resultSource = activeRun?.group_analysis.source ?? group?.source;
  const result = job?.result && activeRun && resultSource
    ? parseGroupAnalysisResult(job.result, projectId, activeRun.group_analysis.group_id, {
      groupVersion: activeRun.group_analysis.group_version,
      source: resultSource,
      snapshotHash: activeRun.group_analysis.snapshot_hash,
    })
    : null;
  const historical = Boolean(
    result && result.state === "available" && (
      !group
      || result.group.version !== group.version
      || !sameGroupAnalysisSource(result.source, group.source)
    )
  );
  const preflightProblem = !preflight
    ? ""
    : !group || !preflightMatchesGroup(preflight, group)
      ? GROUP_CHANGED_TEXT
      : groupBusy
        ? BUSY_PREFLIGHT_TEXT
        : !sourceMatches
          ? SOURCE_PREFLIGHT_TEXT
          : dirty
            ? DIRTY_PREFLIGHT_TEXT
            : "";
  const canUse = Boolean(
    loader && ((group && sourceMatches) || (!group && hasUnsavedSelection)),
  );
  const status = job?.status ?? "";

  return (
    <section className="space-y-3 rounded border border-border p-3"
             data-testid="group-analysis-section" aria-label="Dodatna analiza grupe">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">Dodatna analiza odabrane grupe</h4>
          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
            Deterministička razrada postojećih podataka i ograničeno čitanje javnih
            službenih stranica. Semantička AI analiza nije aktivirana i neće biti
            prikazana kao dovršena.
          </p>
        </div>
        <button
          type="button"
          className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
          data-testid="group-analysis-start"
          disabled={!canUse || busy || groupBusy || !loader}
          onClick={() => void startFlow()}
        >
          {busy
            ? "Provjeravam…"
            : (!group || dirty)
              ? "Spremi grupu i pokreni analizu"
              : "Pokreni dodatnu analizu"}
        </button>
      </div>

      {!group && (
        <p className="text-xs text-muted-foreground" data-testid="group-analysis-group-required">
          {hasUnsavedSelection
            ? "Odabir još nije spremljen. Gumb će ga prvo spremiti kao projektnu grupu, pa otvoriti preflight."
            : "Prvo odaberite zapise i spremite grupu na projekt. Nespremljeni odabir nije izvršni unos."}
        </p>
      )}
      {group && !sourceMatches && (
        <p className="text-xs text-warn" role="alert" data-testid="group-analysis-source-mismatch">
          Grupa pripada drugoj izvornoj analizi. Otvorite njezinu izvornu analizu prije
          pokretanja; odabir se ne prebacuje na trenutačni rezultat.
        </p>
      )}
      {error && (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-sm"
           role="alert" data-testid="group-analysis-error">
          {error}
        </p>
      )}
      {notice && (
        <p className="text-xs text-muted-foreground" role="status"
           data-testid="group-analysis-notice">
          {notice}
        </p>
      )}
      {loading && <p className="text-xs text-muted-foreground">Učitavanje dodatnih analiza…</p>}

      {preflightOpen && preflight && (
        <div className="space-y-3 rounded border border-border bg-muted/20 p-3"
             data-testid="group-analysis-preflight">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h5 className="text-sm font-semibold">Preflight — točan spremljeni kontekst</h5>
              <p className="text-xs text-muted-foreground">
                {preflight.group.name} · v{preflight.group.version} ·{" "}
                {preflight.group.organization_count} organizacija · izvor{" "}
                <span className="font-mono break-all">{preflight.source.job_id}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Odluke <span className="font-mono break-all">{preflight.source.decisions_hash}</span>
                {" · "}referentni datum {preflight.source.evaluated_on}
              </p>
            </div>
            <button type="button" className="rounded border border-border px-3 py-2 text-sm"
                    data-testid="group-analysis-cancel" disabled={busy}
                    onClick={() => { setPreflightOpen(false); idempotencyKey.current = null; }}>
              Odustani
            </button>
          </div>
          <label className="block text-sm">
            Pitanje ili bilješka za ovu analizu (najviše 2000 znakova)
            <textarea
              rows={3}
              maxLength={preflight.question.max_length}
              className="mt-1 block w-full rounded border border-border bg-background px-3 py-2 text-sm"
              data-testid="group-analysis-question"
              value={question}
              disabled={busy}
              onChange={(event) => setQuestion(event.target.value)}
            />
          </label>
          <div className="rounded border border-border p-2 text-xs">
            <p className="font-medium">Službene stranice po organizaciji</p>
            <ul className="mt-1 space-y-1">
              {preflight.organizations.map((item) => (
                <li key={item.organization_id} data-testid="group-analysis-org">
                  <span className="font-medium">{item.organization_name ?? item.organization_id}</span>
                  {" — "}
                  {item.official_host.status === "eligible"
                    ? <span>{item.official_host.host} (dokazano izvorom)</span>
                    : <span className="text-warn">nije potvrđena službena stranica ({item.official_host.reason})</span>}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="group-analysis-semantic">
            Semantička analiza: <strong>nije evaluirana</strong> — model/provider nije
            konfiguriran za proizvod. Deterministička obrada i javne stranice mogu se
            izvršiti bez njega; trošak modela ostaje nepoznat, ne nula.
          </p>
          <p className="text-xs text-muted-foreground">
            Ograničenja: do 20 organizacija, 5 stranica po organizaciji, dubina 1,
            1 MiB po stranici, 10 s po zahtjevu, 5 min po pokretanju, 2 paralelna
            zahtjeva, do 3 preusmjeravanja. Samo javne stranice; bez prijave, kolačića,
            formi, plaćenih servisa, pretrage, kontakata i outreacha.
          </p>
          {preflightProblem && (
            <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-sm"
               role="alert" data-testid="group-analysis-preflight-conflict">
              {preflightProblem}
            </p>
          )}
          <button
            type="button"
            className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
            data-testid="group-analysis-confirm"
            disabled={busy || !loader || Boolean(preflightProblem)}
            onClick={() => void confirmStart()}
          >
            {busy ? "Predajem posao…" : "Potvrdi i pokreni"}
          </button>
        </div>
      )}

      {job && (
        <div className="space-y-3" data-testid="group-analysis-job">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded border border-border px-2 py-1 text-xs"
                  data-testid="group-analysis-status" data-status={status}>
              Status posla: {STATUS_HR[status] ?? status}
            </span>
            <span className="font-mono text-xs text-muted-foreground">{job.id}</span>
            {runs.length > 1 && (
              <label className="text-xs">
                Prijašnja pokretanja
                <select className="ml-2 rounded border border-border bg-background px-2 py-1"
                        value={job.id} onChange={(event) => void openRun(event.target.value)}>
                  {runs.map((run) => (
                    <option key={run.id} value={run.id}>{`${run.id} · ${STATUS_HR[run.status] ?? run.status}`}</option>
                  ))}
                </select>
              </label>
            )}
            {TERMINAL.has(status) && (
              <button type="button" className="rounded border border-border px-3 py-1.5 text-sm"
                      data-testid="group-analysis-rerun" disabled={busy || groupBusy}
                      onClick={() => { idempotencyKey.current = null; void startFlow(); }}>
                Pokreni ponovno kao novi posao
              </button>
            )}
          </div>
          {!TERMINAL.has(status) && (
            <div className="text-xs text-muted-foreground" data-testid="group-analysis-progress">
              <p>Posao je u trajnom redu. Prikaz je stvarnog statusa; nema umjetnog postotka.</p>
              <ul className="mt-1 space-y-1">
                {(job.steps ?? []).map((step) => (
                  <li key={step.id}>{step.step}: {step.status}</li>
                ))}
              </ul>
            </div>
          )}
          {status === "succeeded" && result && result.state === "available" && (
            <div className="space-y-3" data-testid="group-analysis-result">
              <p
                className={`rounded border p-2 text-xs ${
                  historical
                    ? "border-warn/40 bg-warn/10 text-warn"
                    : "border-border bg-muted/20 text-muted-foreground"
                }`}
                data-testid={historical ? "group-analysis-historical" : "group-analysis-result-identity"}
              >
                {historical
                  ? "Povijesni rezultat — ovo nisu trenutačni nalazi. "
                  : "Rezultat je vezan na točan spremljeni kontekst. "}
                Verzija grupe v{result.group.version} · izvor{" "}
                <span className="font-mono break-all">{result.source.job_id}</span>
                {" · "}odluke{" "}
                <span className="font-mono break-all">{result.source.decisions_hash}</span>
                {" · "}odabir {result.group.organization_count} organizacija
              </p>
              <div className="flex flex-wrap gap-3 text-xs">
                <span>Organizacije: {result.summary.organizations}</span>
                <span>Dokazani hostovi: {result.summary.eligible_hosts}</span>
                <span>Pročitane stranice: {result.summary.pages_fetched}</span>
                <span>Nedostupne: {result.summary.pages_unavailable}</span>
                <span>Novi nalazi: {result.summary.new_observations}</span>
                <span>Sukobi: {result.summary.conflicts}</span>
              </div>
              <p className="rounded border border-warn/40 bg-warn/10 p-2 text-xs" data-testid="group-analysis-semantic-result">
                Semantička analiza: <strong>nije evaluirana</strong>. Ovo su deterministički
                izvodi i javne stranice, ne cjelovita AI analiza.
              </p>
              {result.cost && (
                <p className="text-xs text-muted-foreground" data-testid="group-analysis-cost">
                  Izmjereni trošak dovršenog besplatnog rada: {formatMicros(result.cost.actual_micros)}.
                  Trošak semantičkog modela: nepoznat (provider nije konfiguriran).
                </p>
              )}
              <div className="space-y-2">
                {result.organizations.map((organization) => (
                  <details key={organization.organization_id} className="rounded border border-border p-2"
                           data-testid="group-analysis-organization">
                    <summary className="cursor-pointer text-sm font-medium">
                      {organization.organization_name ?? organization.organization_id}
                      {" · "}{organization.official_site.state}
                    </summary>
                    <div className="mt-2 space-y-2 text-xs">
                      <p>
                        Postojeći podaci: {organization.existing_evidence.supported_facts.length} potvrđenih,
                        {" "}{organization.existing_evidence.missing_fields.length} nedostajućih.
                      </p>
                      {organization.official_site.pages.map((page) => (
                        <div key={page.url} className="rounded border border-border p-2">
                          <p className="break-all font-mono">{page.url}</p>
                          <p>Preuzeto: {page.fetched_at} · hash: {page.content_hash}</p>
                          {page.title && <p className="font-medium">{page.title}</p>}
                          {page.observations.map((observation, index) => (
                            <p key={`${page.url}:${index}`}>– {observation.text}</p>
                          ))}
                        </div>
                      ))}
                      {organization.official_site.limitations.length > 0 && (
                        <p className="text-warn">Ograničenja: {organization.official_site.limitations.join(", ")}</p>
                      )}
                      <p>Usporedba: {organization.comparison.state}</p>
                      {organization.comparison.items.map((item) => (
                        <p key={`${item.field}:${item.page_url}`}>
                          {item.field}: {item.prior_value} → {item.observed_value} ({item.relation})
                        </p>
                      ))}
                      {organization.inference.length > 0 && (
                        <div>
                          <p className="font-medium">Zaključci (deterministički, nisu činjenice):</p>
                          {organization.inference.map((item, index) => (
                            <p key={`${item.kind}:${index}`}>– {item.text}</p>
                          ))}
                        </div>
                      )}
                      {organization.conflicts.length > 0 && (
                        <p className="text-warn">Sukobi: {organization.conflicts.map((item) => item.field).join(", ")}</p>
                      )}
                      {organization.unknowns.length > 0 && (
                        <p>Nepoznanice: {organization.unknowns.map((item) => item.reason).join(", ")}</p>
                      )}
                      <p>Semantička analiza: {organization.semantic_analysis}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
          {status === "succeeded" && result && result.state === "unavailable" && (
            <p className="text-sm text-warn" data-testid="group-analysis-result-unavailable">
              Rezultat nije dostupan ({result.reason}). Nema izmišljenih nalaza.
            </p>
          )}
          {status !== "succeeded" && TERMINAL.has(status) && (
            <p className="text-sm text-warn" data-testid="group-analysis-terminal">
              Posao je završio statusom {STATUS_HR[status] ?? status}; nema potvrđenih
              nalaza iz ovog pokretanja.
            </p>
          )}
          {status === "succeeded" && job.result && !result && (
            <p className="text-sm text-destructive" role="alert">
              Rezultat posla nije prošao strogu provjeru i ne prikazuje se.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
